import React, { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import Mention from "@tiptap/extension-mention";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import { Box, CircularProgress, Divider, IconButton, Tooltip } from "@mui/material";
import { Bold, Italic, Strikethrough, Code, List, ListOrdered, Quote, Heading2, Link2, ImagePlus, Table2, Undo2, Redo2, SquareCode } from "lucide-react";
import { T, TYPE } from "../user/complaints/shared";
import { usePm } from "./PmContext";
import { fileViewUrl } from "./api";

// TipTap editor used for issue descriptions, comments and project discussion.
// Output is HTML that the server sanitises again (never trusted as-is).
// @mentions cover people and project roles ("role:<id>"); images upload
// through the caller's `onUpload(files) -> [{viewToken, fileName}]`.

const suggestionRenderer = () => {
  let el = null;
  let items = [];
  let index = 0;
  let command = null;
  const draw = () => {
    if (!el) return;
    el.innerHTML = "";
    if (!items.length) {
      const empty = document.createElement("div");
      empty.textContent = "No matches";
      empty.style.cssText = "padding:8px 12px;color:#98A2B3;font-size:13px";
      el.appendChild(empty);
      return;
    }
    items.forEach((it, i) => {
      const row = document.createElement("div");
      row.textContent = `${it.label}${it.hint ? ` · ${it.hint}` : ""}`;
      row.style.cssText = `padding:7px 12px;cursor:pointer;font-size:13px;border-radius:6px;${i === index ? "background:#EEF2FF;color:#4F46E5" : "color:#101828"}`;
      row.onmousedown = (e) => {
        e.preventDefault();
        command({ id: it.id, label: it.label });
      };
      el.appendChild(row);
    });
  };
  const place = (rect) => {
    if (!el || !rect) return;
    const r = rect();
    if (!r) return;
    el.style.left = `${Math.min(r.left + window.scrollX, window.innerWidth - 260)}px`;
    el.style.top = `${r.bottom + window.scrollY + 4}px`;
  };
  return {
    onStart: (props) => {
      el = document.createElement("div");
      el.style.cssText = "position:absolute;z-index:2000;min-width:220px;max-height:260px;overflow:auto;background:#fff;border:1px solid #E4E7EC;border-radius:10px;box-shadow:0 12px 24px rgba(16,24,40,.14);padding:4px";
      document.body.appendChild(el);
      items = props.items;
      index = 0;
      command = props.command;
      draw();
      place(props.clientRect);
    },
    onUpdate: (props) => {
      items = props.items;
      index = 0;
      command = props.command;
      draw();
      place(props.clientRect);
    },
    onKeyDown: ({ event }) => {
      if (event.key === "ArrowDown") {
        index = (index + 1) % Math.max(items.length, 1);
        draw();
        return true;
      }
      if (event.key === "ArrowUp") {
        index = (index - 1 + items.length) % Math.max(items.length, 1);
        draw();
        return true;
      }
      if (event.key === "Enter" && items[index]) {
        command({ id: items[index].id, label: items[index].label });
        return true;
      }
      if (event.key === "Escape") {
        el?.remove();
        el = null;
        return true;
      }
      return false;
    },
    onExit: () => {
      el?.remove();
      el = null;
    },
  };
};

const Btn = ({ title, onClick, active, disabled, children }) => (
  <Tooltip title={title}>
    <span>
      <IconButton size="small" onMouseDown={(e) => e.preventDefault()} onClick={onClick} disabled={disabled}
        sx={{ borderRadius: "6px", color: active ? T.brand : T.inkSecondary, bgcolor: active ? T.brandSoft : "transparent", p: 0.6 }}>
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

export const RICH_CSS = {
  fontSize: TYPE.body, color: T.ink, lineHeight: 1.6, wordBreak: "break-word",
  "& p": { my: 0.6 }, "& h1,& h2,& h3": { mt: 1.5, mb: 0.75, lineHeight: 1.3 }, "& h2": { fontSize: "1.1rem" }, "& h3": { fontSize: "1rem" },
  "& ul,& ol": { pl: 3, my: 0.5 }, "& blockquote": { borderLeft: `3px solid ${T.border}`, pl: 1.5, ml: 0, color: T.inkSecondary },
  "& pre": { bgcolor: "#0F172A", color: "#E2E8F0", p: 1.5, borderRadius: "8px", overflowX: "auto", fontSize: "0.8rem" },
  "& code": { bgcolor: T.track, px: 0.5, borderRadius: "4px", fontSize: "0.82rem" }, "& pre code": { bgcolor: "transparent", p: 0 },
  "& img": { maxWidth: "100%", borderRadius: "8px", border: `1px solid ${T.border}` },
  "& a": { color: T.brand },
  "& table": { borderCollapse: "collapse", width: "100%", my: 1, tableLayout: "fixed" },
  "& td,& th": { border: `1px solid ${T.border}`, p: 0.75, verticalAlign: "top", minWidth: 60 }, "& th": { bgcolor: T.surfaceMuted, fontWeight: 700 },
  "& .mention": { bgcolor: T.brandSoft, color: T.brand, borderRadius: "4px", px: 0.4, fontWeight: 600 },
};

/**
 * @param value html
 * @param onChange (html) => void
 * @param mentionRoles include project roles as mention targets
 */
const RichEditor = ({ value, onChange, placeholder = "Write something… (@ to mention)", onUpload, minHeight = 110, autoFocus = false, mentionRoles = [], onSubmit, editorRef }) => {
  const { people } = usePm();
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const sourceRef = useRef({ people, mentionRoles });
  sourceRef.current = { people, mentionRoles };
  const submitRef = useRef(onSubmit);
  submitRef.current = onSubmit;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer nofollow", target: "_blank" } }),
      Image,
      Placeholder.configure({ placeholder }),
      Table.configure({ resizable: false }),
      TableRow, TableHeader, TableCell,
      Mention.configure({
        HTMLAttributes: { class: "mention" },
        renderLabel: ({ node }) => `@${node.attrs.label ?? node.attrs.id}`,
        suggestion: {
          items: ({ query }) => {
            const qq = query.toLowerCase();
            const { people: ps, mentionRoles: rs } = sourceRef.current;
            const list = [
              ...ps.map((p) => ({ id: String(p.id), label: p.name, hint: p.email })),
              ...rs.map((r) => ({ id: `role:${r._id}`, label: r.name, hint: "role" })),
            ];
            return list.filter((x) => x.label.toLowerCase().includes(qq)).slice(0, 8);
          },
          render: suggestionRenderer,
        },
      }),
    ],
    content: value || "",
    autofocus: autoFocus ? "end" : false,
    onUpdate: ({ editor: e }) => onChange?.(e.isEmpty ? "" : e.getHTML()),
    editorProps: {
      handleKeyDown: (_view, event) => {
        if (submitRef.current && event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
          submitRef.current();
          return true;
        }
        return false;
      },
      handlePaste: (_view, event) => {
        const files = [...(event.clipboardData?.files || [])].filter((f) => f.type.startsWith("image/"));
        if (files.length && onUpload) {
          insertImages(files);
          return true;
        }
        return false;
      },
    },
  });

  // external value resets (e.g. after submit)
  useEffect(() => {
    if (editor && value !== undefined && value !== (editor.isEmpty ? "" : editor.getHTML())) editor.commands.setContent(value || "", false);
  }, [value, editor]);
  useEffect(() => {
    if (editorRef) editorRef.current = editor;
  }, [editor, editorRef]);

  const insertImages = async (files) => {
    if (!onUpload) return;
    setUploading(true);
    try {
      const saved = await onUpload(files);
      for (const f of saved || []) if (f.viewToken) editor?.chain().focus().setImage({ src: fileViewUrl(f.viewToken), alt: f.fileName }).run();
    } finally {
      setUploading(false);
    }
  };

  const setLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("Link URL", prev || "https://");
    if (url === null) return;
    if (!url) editor.chain().focus().extendMarkRange("link").unsetLink().run();
    else if (/^(https?:|mailto:)/i.test(url)) editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  if (!editor) return null;
  return (
    <Box sx={{ border: `1px solid ${T.borderStrong}`, borderRadius: T.radiusControl, bgcolor: "#fff", "&:focus-within": { borderColor: T.brand, boxShadow: `0 0 0 3px ${T.brandSoft}` } }}>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.25, px: 0.75, py: 0.5, borderBottom: `1px solid ${T.border}` }}>
        <Btn title="Bold (Ctrl+B)" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()}><Bold size={15} /></Btn>
        <Btn title="Italic (Ctrl+I)" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()}><Italic size={15} /></Btn>
        <Btn title="Strikethrough" active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()}><Strikethrough size={15} /></Btn>
        <Btn title="Heading" active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Heading2 size={15} /></Btn>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
        <Btn title="Bullet list" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()}><List size={15} /></Btn>
        <Btn title="Numbered list" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()}><ListOrdered size={15} /></Btn>
        <Btn title="Quote" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()}><Quote size={15} /></Btn>
        <Btn title="Inline code" active={editor.isActive("code")} onClick={() => editor.chain().focus().toggleCode().run()}><Code size={15} /></Btn>
        <Btn title="Code block" active={editor.isActive("codeBlock")} onClick={() => editor.chain().focus().toggleCodeBlock().run()}><SquareCode size={15} /></Btn>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
        <Btn title="Link" active={editor.isActive("link")} onClick={setLink}><Link2 size={15} /></Btn>
        <Btn title="Table" onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}><Table2 size={15} /></Btn>
        {onUpload && (
          <Btn title="Insert image" disabled={uploading} onClick={() => fileRef.current?.click()}>
            {uploading ? <CircularProgress size={14} /> : <ImagePlus size={15} />}
          </Btn>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Btn title="Undo" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()}><Undo2 size={15} /></Btn>
        <Btn title="Redo" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()}><Redo2 size={15} /></Btn>
        <input ref={fileRef} type="file" accept="image/*" hidden multiple onChange={(e) => { insertImages([...e.target.files]); e.target.value = ""; }} />
      </Box>
      <Box sx={{ px: 1.5, py: 1, minHeight, cursor: "text", ...RICH_CSS, "& .ProseMirror": { outline: "none", minHeight: minHeight - 16 }, "& .ProseMirror p.is-editor-empty:first-of-type::before": { content: "attr(data-placeholder)", color: T.inkMuted, float: "left", height: 0, pointerEvents: "none" } }}
        onClick={() => editor.chain().focus().run()}>
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
};

/** Read-only rendering of server-sanitised rich text. */
export const RichView = ({ html, empty = "No description" }) =>
  html ? <Box sx={RICH_CSS} dangerouslySetInnerHTML={{ __html: html }} /> : <Box sx={{ color: T.inkMuted, fontSize: TYPE.body }}>{empty}</Box>;

export default RichEditor;
